/**
 * Reusable fixed tooltip for a container with SVG (or other) markers.
 *
 * - Markers: any element with a data attribute (default data-anchor-tooltip="suffix")
 *   pointing to <template id="{templatePrefix}{suffix}">; clone HTML into the panel.
 * - Panel: a single #tooltipId in the same root, with .anchor-tooltip__body and __arrow.
 * - Position: centers above a marker; uses first <circle> in marker if present, else
 *   the marker’s bounding box. Reads --anchor-tooltip-gap-to-marker and
 *   --anchor-tooltip-arrow-tip-offset from computed style (inherited from root).
 *
 * Map uses init with templatePrefix "map-tooltip-" and attribute "data-map-tooltip".
 * New blocks can use defaults and "anchor-tooltip-" templates.
 */
const GAP = "--anchor-tooltip-gap-to-marker";
const OFFSET = "--anchor-tooltip-arrow-tip-offset";
const DEFAULT_BODY = ".anchor-tooltip__body";

function readTipMetric(tip, name, fallback) {
  const v = getComputedStyle(tip).getPropertyValue(name).trim();
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param {Element} marker
 * @param {HTMLElement} tip
 */
function positionAnchorTooltip(marker, tip) {
  const target =
    typeof marker.querySelector === "function"
      ? marker.querySelector("circle")
      : null;
  const r =
    (target && target.getBoundingClientRect()) ||
    marker.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const gap = readTipMetric(tip, GAP, 8);
  const tipOffset = readTipMetric(tip, OFFSET, 0);
  const bottomY = r.top - gap + tipOffset;
  const h = tip.offsetHeight;
  const w = tip.offsetWidth;

  tip.style.left = `${Math.round(cx - w / 2)}px`;
  tip.style.top = `${Math.round(bottomY - h)}px`;
}

/**
 * @param {Element|string} root
 * @param {object} options
 * @param {string} options.tooltipId
 * @param {string} [options.templatePrefix] default "anchor-tooltip-"
 * @param {string} [options.markerSelector] e.g. "[data-anchor-tooltip]" or "[data-map-tooltip]"
 * @param {string} [options.markerDataAttr] long form for reading suffix, e.g. "data-map-tooltip" (must match selector)
 * @param {string} [options.bodySelector] default .anchor-tooltip__body
 * @param {boolean} [options.persistent] if true, no hover: show the first marker’s template on load and keep it (reposition on scroll/resize)
 * @param {() => boolean} [options.canShowHoverTooltip] if false, hover does not open the panel (tap/click unaffected)
 */
function initAnchorTooltips(root, options) {
  const {
    tooltipId,
    templatePrefix = "anchor-tooltip-",
    markerSelector = "[data-anchor-tooltip]",
    markerDataAttr = "data-anchor-tooltip",
    bodySelector = DEFAULT_BODY,
    persistent = false,
    canShowHoverTooltip,
  } = options;

  const el = typeof root === "string" ? document.querySelector(root) : root;
  if (!el) {
    return;
  }

  const tip = document.getElementById(tooltipId);
  if (!tip) {
    return;
  }

  const bodyEl = tip.querySelector(bodySelector);
  if (!bodyEl) {
    return;
  }

  const markers = el.querySelectorAll(markerSelector);
  if (!markers.length) {
    return;
  }

  let active = null;
  let boundReposition = null;
  const imgLoadHandlers = new WeakMap();
  let closeTransitionCleanup = null;
  let closeFallbackId = 0;
  let interactionGeneration = 0;

  const clearClosePending = () => {
    if (closeTransitionCleanup) {
      tip.removeEventListener("transitionend", closeTransitionCleanup);
      closeTransitionCleanup = null;
    }
    if (closeFallbackId) {
      clearTimeout(closeFallbackId);
      closeFallbackId = 0;
    }
  };

  const finalizeHide = (closeId) => {
    if (closeId !== interactionGeneration) {
      return;
    }
    clearClosePending();
    active = null;
    tip.hidden = true;
    bodyEl.replaceChildren();
  };

  const hide = () => {
    if (!tip.classList.contains("is-open")) {
      return;
    }
    if (boundReposition) {
      window.removeEventListener("scroll", boundReposition, true);
      window.removeEventListener("resize", boundReposition);
      boundReposition = null;
    }
    bodyEl.querySelectorAll("img").forEach((img) => {
      const fn = imgLoadHandlers.get(img);
      if (fn) {
        img.removeEventListener("load", fn);
        imgLoadHandlers.delete(img);
      }
    });
    clearClosePending();
    active = null;
    const closeId = (interactionGeneration += 1);
    tip.classList.remove("is-open");
    const onEnd = (e) => {
      if (e.target !== tip) {
        return;
      }
      if (e.propertyName !== "opacity" && e.propertyName !== "transform") {
        return;
      }
      tip.removeEventListener("transitionend", onEnd);
      closeTransitionCleanup = null;
      finalizeHide(closeId);
    };
    closeTransitionCleanup = onEnd;
    tip.addEventListener("transitionend", onEnd);
    closeFallbackId = window.setTimeout(() => {
      closeFallbackId = 0;
      finalizeHide(closeId);
    }, 300);
  };

  const show = (marker) => {
    const suffix = marker.getAttribute(markerDataAttr);
    if (!suffix) {
      return;
    }
    const tpl = document.getElementById(templatePrefix + suffix);
    if (!tpl || !tpl.content) {
      return;
    }
    clearClosePending();
    interactionGeneration += 1;
    active = marker;
    bodyEl.replaceChildren();
    bodyEl.appendChild(tpl.content.cloneNode(true));
    tip.hidden = false;
    requestAnimationFrame(() => {
      positionAnchorTooltip(marker, tip);
      void tip.offsetWidth;
      tip.classList.add("is-open");
      bodyEl.querySelectorAll("img").forEach((img) => {
        if (img.complete) {
          return;
        }
        const onLoad = () => {
          if (active === marker) {
            positionAnchorTooltip(marker, tip);
          }
        };
        img.addEventListener("load", onLoad, { once: true });
        imgLoadHandlers.set(img, onLoad);
      });
    });
    boundReposition = () => {
      if (active) {
        positionAnchorTooltip(active, tip);
      }
    };
    window.addEventListener("scroll", boundReposition, true);
    window.addEventListener("resize", boundReposition);
  };

  if (persistent) {
    if (markers.length) {
      show(markers[0]);
    }
  } else {
    const allowHoverTooltip =
      typeof canShowHoverTooltip === "function"
        ? canShowHoverTooltip
        : () => true;
    markers.forEach((marker) => {
      marker.addEventListener("mouseenter", () => {
        if (!allowHoverTooltip()) {
          return;
        }
        show(marker);
      });
      marker.addEventListener("mouseleave", hide);
    });
  }
}

export { initAnchorTooltips, positionAnchorTooltip, readTipMetric };
